import { useCallback, useEffect, useRef, useState } from 'react';

import useApiControl from '../../common/useApiControl';

export const CHAT_MESSAGE_MAX_LENGTH = 200;
export const CHAT_POST_COOLDOWN_MS = 3000;

export const normalizeChatMessages = (messages) => (
  Array.isArray(messages) ? messages.filter((message) => message?.id !== undefined && message?.id !== null) : []
);

export const mergeMessagesById = (current, incoming) => {
  const map = new Map();

  [...normalizeChatMessages(current), ...normalizeChatMessages(incoming)].forEach((message) => {
    map.set(Number(message.id), message);
  });

  return Array.from(map.values()).sort((a, b) => Number(a.id) - Number(b.id));
};

export const getLatestChatMessageId = (messages) => (
  normalizeChatMessages(messages).reduce((latestId, message) => Math.max(latestId, Number(message.id)), 0)
);

const getChatErrorMessage = (error, fallback) => {
  const responseMessage = error?.response?.data?.message;
  const validationMessages = error?.response?.data?.errors?.message;

  if (Array.isArray(validationMessages) && validationMessages.length > 0) return validationMessages[0];
  if (responseMessage) return responseMessage;
  if (error?.response?.status === 401 || error?.response?.status === 403) return 'ログインまたは権限を確認してください。';

  return fallback;
};

export default function useRoomChat({ chatRoomId, sseData, limit = 50 } = {}) {
  const [messages, setMessages] = useState([]);
  const [fetchError, setFetchError] = useState('');
  const [postError, setPostError] = useState('');
  const [postSuccessMessage, setPostSuccessMessage] = useState('');
  const [postCooldown, setPostCooldown] = useState(false);
  const lastIdRef = useRef(null);
  const postCooldownTimerRef = useRef(null);

  const { loading, executeRequest: executeGetChatMessages } = useApiControl({ action: '/get_chat_messages', method: 'POST' });
  const { executeRequest: executeGetChatUpdates } = useApiControl({ action: '/get_chat_updates', method: 'POST' });
  const { loading: posting, executeRequest: executePostChatMessage } = useApiControl({ action: '/post_chat_message', method: 'POST' });

  const startPostCooldown = useCallback(() => {
    if (postCooldownTimerRef.current) {
      clearTimeout(postCooldownTimerRef.current);
    }

    setPostCooldown(true);
    postCooldownTimerRef.current = setTimeout(() => {
      setPostCooldown(false);
      postCooldownTimerRef.current = null;
    }, CHAT_POST_COOLDOWN_MS);
  }, []);

  const replaceMessages = useCallback((incomingMessages) => {
    const nextMessages = mergeMessagesById([], incomingMessages);
    lastIdRef.current = nextMessages.length > 0 ? getLatestChatMessageId(nextMessages) : null;
    setMessages(nextMessages);
  }, []);

  const appendMessages = useCallback((incomingMessages) => {
    const normalizedIncoming = normalizeChatMessages(incomingMessages);
    if (normalizedIncoming.length === 0) return;

    setMessages((currentMessages) => {
      const nextMessages = mergeMessagesById(currentMessages, normalizedIncoming);
      lastIdRef.current = nextMessages.length > 0 ? getLatestChatMessageId(nextMessages) : null;
      return nextMessages;
    });
  }, []);

  const fetchUpdates = useCallback(async () => {
    if (!chatRoomId) return;

    try {
      const result = await executeGetChatUpdates({ chat_room_id: chatRoomId, last_id: lastIdRef.current ?? 0 });
      appendMessages(result?.messages);
      setFetchError('');
    } catch (error) {
      console.error('チャット差分取得に失敗しました。', error);
      setFetchError(getChatErrorMessage(error, 'チャットの更新取得に失敗しました。'));
    }
  }, [appendMessages, chatRoomId, executeGetChatUpdates]);

  const postMessage = useCallback(async (message) => {
    if (posting || postCooldown) {
      return false;
    }

    if (!chatRoomId) {
      setPostError('チャットルーム情報を取得できていません。');
      return false;
    }

    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      setPostError('メッセージを入力してください。');
      return false;
    }

    if (trimmedMessage.length > CHAT_MESSAGE_MAX_LENGTH) {
      setPostError(`${CHAT_MESSAGE_MAX_LENGTH}文字以内で入力してください。`);
      return false;
    }

    try {
      setPostError('');
      setPostSuccessMessage('');
      const result = await executePostChatMessage({ chat_room_id: chatRoomId, message: trimmedMessage });

      if (result?.message) appendMessages([result.message]);
      else await fetchUpdates();

      setPostSuccessMessage('チャットを投稿しました。');
      startPostCooldown();
      return true;
    } catch (error) {
      console.error('チャット投稿に失敗しました。', error);
      setPostError(getChatErrorMessage(error, 'チャット投稿に失敗しました。'));
      return false;
    }
  }, [appendMessages, chatRoomId, executePostChatMessage, fetchUpdates, postCooldown, posting, startPostCooldown]);

  useEffect(() => {
    if (!chatRoomId) {
      replaceMessages([]);
      setFetchError('チャットルーム情報を取得できていません。');
      return;
    }

    executeGetChatMessages({ chat_room_id: chatRoomId, limit }).then((result) => {
      replaceMessages(result?.messages);
      setFetchError('');
    }).catch((error) => {
      console.error('チャット初期取得に失敗しました。', error);
      setFetchError(getChatErrorMessage(error, 'チャットの取得に失敗しました。'));
    });
  }, [chatRoomId, executeGetChatMessages, limit, replaceMessages]);

  useEffect(() => {
    if (sseData?.type !== 'system' || sseData.action !== 'sync' || sseData.target !== 'chatMessages') return;

    const latestId = Number(sseData?.payload?.latest_id);
    if (Number.isInteger(latestId) && lastIdRef.current !== null && latestId <= lastIdRef.current) return;

    fetchUpdates();
  }, [fetchUpdates, sseData]);

  useEffect(() => () => {
    if (postCooldownTimerRef.current) {
      clearTimeout(postCooldownTimerRef.current);
    }
  }, []);

  return { fetchError, loading, messages, postCooldown, postError, postMessage, postSuccessMessage, posting };
}
